import { v4 } from "uuid";
import { ChatStore } from "@/stores/ChatStore";
import { fetchSessionId } from "./fetch_session_id";
import extractExternalLinks from "@/lib/extractExternalLinks";
import { TInfoBox } from "@/types/TInfoBox";
import mapExtractedExternalLinks from "@/lib/mapExtractedExternalLinks";
import { isBrokerQuery, isClaimStatusQuery } from "./intent-detection";

const failedRequestMessage = "Das hat leider nicht geklappt.";

export async function fetchCustomerSupportResponse(message: string) {
  console.log("🚀 [fetchCustomerSupportResponse] START", {
    message,
    messageLength: message.length,
    timestamp: new Date().toISOString()
  });

  // Check if interruption is already in progress
  if (ChatStore.getState().isForcedToInterrupt) {
    console.log("⚠️ [fetchCustomerSupportResponse] Request aborted due to interruption before starting");
    return;
  }

  // Create AbortController and register it with the store
  const controller = new AbortController();
  ChatStore.getState().registerAbortController(controller);
  console.log("✅ [fetchCustomerSupportResponse] AbortController created and registered");

  try {
    // Fetch session ID with the same controller
    console.log("🔑 [fetchCustomerSupportResponse] Fetching session ID...");
    const sessionId = await fetchSessionId();
    console.log("✅ [fetchCustomerSupportResponse] Session ID obtained:", sessionId);
    
    // Check if interrupted during session ID fetch
    if (controller.signal.aborted) {
      console.log("⚠️ [fetchCustomerSupportResponse] Request aborted during session fetch");
      return;
    }
    
    const endpoint = `/api/schadenki/session/${sessionId}/interaction`;
    console.log("📡 [fetchCustomerSupportResponse] Sending POST request to:", endpoint);
    console.log("📦 [fetchCustomerSupportResponse] Request body:", { message });
    
    const response = await fetch(
      endpoint,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message
        }),
        signal: controller.signal
      }
    );
    
    console.log("📥 [fetchCustomerSupportResponse] Response received:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      console.error(`❌ [fetchCustomerSupportResponse] Server error: ${response.status} ${response.statusText}`);
      const errorText = await response.text();
      console.error("❌ [fetchCustomerSupportResponse] Error response body:", errorText);
      appendAssistantMessage(failedRequestMessage);
      return;
    }
    
    const data = await response.json();
    console.log("✅ [fetchCustomerSupportResponse] Response data:", data);
    
    const sentenceToSay = data?.instructions?.[0]?.message ?? failedRequestMessage;
    console.log("💬 [fetchCustomerSupportResponse] Extracted message:", sentenceToSay);
    
    if (isBrokerQuery(message)) {
      console.log("🔍 [fetchCustomerSupportResponse] Broker query detected - opening validation dialog");
      // Override generic response with prompt to enter credentials
      const brokerPrompt = "Um Ihre Vermittler-Informationen abzurufen, benötige ich Ihre Versicherungsscheinnummer und Ihre Postleitzahl.";
      appendAssistantMessage(brokerPrompt);
      
      // Open broker validation dialog after a short delay (so user sees the message first)
      setTimeout(() => {
        ChatStore.getState().setBrokerValidationDialogOpen(true);
      }, 1000);
    } else if (isClaimStatusQuery(message)) {
      console.log("🔍 [fetchCustomerSupportResponse] Claim status query detected - opening claim status dialog");
      // Override generic response with prompt to enter credentials
      const claimStatusPrompt = "Um den Status Ihres Schadens abzurufen, benötige ich Ihre Schadennummer und Ihre Postleitzahl.";
      appendAssistantMessage(claimStatusPrompt);
      
      // Open claim status dialog after a short delay (so user sees the message first)
      setTimeout(() => {
        ChatStore.getState().setClaimStatusDialogOpen(true);
      }, 1000);
    } else {
      appendAssistantMessage(sentenceToSay);
    }
    
    console.log("✅ [fetchCustomerSupportResponse] Message appended successfully");
  } catch (error) {
    // Check if this was an abort error
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.log('⚠️ [fetchCustomerSupportResponse] Fetch aborted due to user interruption');
      return;
    }
    
    console.error('❌ [fetchCustomerSupportResponse] Error occurred:', error);
    console.error('❌ [fetchCustomerSupportResponse] Error stack:', error instanceof Error ? error.stack : 'N/A');
    appendAssistantMessage(failedRequestMessage);
  } finally {
    // Clean up controller reference from the store
    const controllers = ChatStore.getState().abortControllers;
    const index = controllers.indexOf(controller);
    if (index > -1) {
      controllers.splice(index, 1);
      ChatStore.setState({ abortControllers: [...controllers] });
    }
    console.log("🧹 [fetchCustomerSupportResponse] Cleanup complete");
  }
}

const appendAssistantMessage = (message: string) => {
  ChatStore.getState().addMessage({
    id: v4(),
    sender: "assistant",
    transcript: message,
    timestamp: new Date()
  });
  ChatStore.getState().appendDeltaBuffer(message);

  const externalLinks = extractExternalLinks(message);
  if (externalLinks.length > 0) {
    const sortedByBrand: TInfoBox[] = mapExtractedExternalLinks(externalLinks);
    ChatStore.getState().setDisplayedExternalLinks(sortedByBrand);
  }
}